<template>
  <div class="app-container dida-push-monitor">
    <dida-module-header
      title="推送时效监控"
      description="统一观察订单回传、资源差异发现、首次尝试、道旅受理以及超时异常的完整时效链路。"
      :status="monitorStatus"
      :status-text="monitorStatusText"
      :updated-at="lastRefreshedAt"
      auto-refresh-text="每 15 秒自动刷新"
      :loading="activeLoading"
      @refresh="refreshActive"
    />
    <el-alert
      title="统一观察本系统向道旅发送的订单回传和资源差异推送。订单请求/响应明细保留 7 天；汇总状态与订单、资源控制记录长期保留。"
      type="info"
      :closable="false"
      show-icon
      class="page-alert"
    />

    <el-tabs v-model="activeTab" type="border-card" @tab-click="handleTabClick">
      <el-tab-pane label="订单状态回传" name="order">
        <div class="filter-container monitor-filters">
          <el-input v-model="feedbackQuery.booking_id" clearable size="small" placeholder="BookingID" @keyup.enter.native="filterFeedback" />
          <el-select v-model="feedbackQuery.supplier" clearable size="small" placeholder="供应商">
            <el-option v-for="item in suppliers" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
          <el-select v-model="feedbackQuery.status" clearable size="small" placeholder="推送状态">
            <el-option v-for="item in feedbackStatuses" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
          <el-switch v-model="feedbackQuery.warning_only" active-text="仅看告警" />
          <el-button type="primary" size="small" icon="el-icon-search" @click="filterFeedback">查询</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="getFeedbackList">刷新</el-button>
        </div>

        <el-alert
          title="订单补推只适用于非立即确认订单，失败后按 1/2/5/10/30 分钟退避，人工补推也不会绕过 1 分钟最小间隔。立即确认订单不允许订单补推；房价、房量等资源补推请在资源发布功能中操作。"
          type="warning"
          :closable="false"
          show-icon
          class="section-alert"
        />

        <el-table v-loading="feedbackLoading" :data="feedbackList" border fit highlight-current-row size="small">
          <el-table-column label="告警" width="75" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" :type="scope.row.warning ? 'danger' : 'success'">{{ scope.row.warning ? '是' : '否' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="booking_id" label="BookingID" min-width="185" show-overflow-tooltip />
          <el-table-column label="供应商" width="100" align="center"><template slot-scope="scope">{{ supplierName(scope.row.supplier) }}</template></el-table-column>
          <el-table-column label="事件" width="115" align="center"><template slot-scope="scope">{{ eventName(scope.row.event_type) }}</template></el-table-column>
          <el-table-column label="目标值" width="90" align="center"><template slot-scope="scope">{{ eventValue(scope.row) }}</template></el-table-column>
          <el-table-column label="状态" min-width="145" align="center">
            <template slot-scope="scope"><el-tag size="mini" :type="feedbackTag(scope.row.status)">{{ scope.row.status_text || scope.row.status }}</el-tag></template>
          </el-table-column>
          <el-table-column prop="eligible_at" label="应推时间" min-width="165" align="center" />
          <el-table-column prop="first_attempt_at" label="首次尝试" min-width="165" align="center"><template slot-scope="scope">{{ scope.row.first_attempt_at || '-' }}</template></el-table-column>
          <el-table-column label="首次延迟" width="100" align="right"><template slot-scope="scope">{{ formatSeconds(scope.row.first_attempt_delay_seconds) }}</template></el-table-column>
          <el-table-column prop="last_attempt_at" label="最近尝试" min-width="165" align="center"><template slot-scope="scope">{{ scope.row.last_attempt_at || '-' }}</template></el-table-column>
          <el-table-column prop="next_attempt_at" label="下次重试" min-width="165" align="center"><template slot-scope="scope">{{ scope.row.next_attempt_at || '-' }}</template></el-table-column>
          <el-table-column label="尝试次数" width="85" prop="attempt_count" align="center" />
          <el-table-column label="总耗时" width="100" align="right"><template slot-scope="scope">{{ formatSeconds(scope.row.total_delay_seconds) }}</template></el-table-column>
          <el-table-column prop="last_error" label="最近错误" min-width="240" show-overflow-tooltip><template slot-scope="scope">{{ scope.row.last_error || '-' }}</template></el-table-column>
          <el-table-column label="操作" width="220" fixed="right" align="center">
            <template slot-scope="scope">
              <el-button type="text" size="mini" @click="showFeedbackDetail(scope.row)">请求明细</el-button>
              <el-button
                v-if="$canEdit && canRetry(scope.row)"
                type="text"
                size="mini"
                :loading="retryingJobID === scope.row.id"
                @click="retryFeedback(scope.row)"
              >订单补推</el-button>
              <span v-else-if="isImmediateConfirmation(scope.row)" class="retry-disabled-text">立即确认，不支持订单补推</span>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            :current-page="feedbackQuery.page"
            :page-sizes="[20, 50, 100]"
            :page-size="feedbackQuery.limit"
            :total="feedbackTotal"
            background
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="changeFeedbackSize"
            @current-change="changeFeedbackPage"
          />
        </div>
      </el-tab-pane>

      <el-tab-pane label="资源差异推送" name="resource">
        <div class="filter-container monitor-filters">
          <el-input v-model="resourceQuery.hotel_code" clearable size="small" placeholder="源酒店编码" @keyup.enter.native="filterResources" />
          <el-select v-model="resourceQuery.supplier" clearable size="small" placeholder="供应商">
            <el-option v-for="item in suppliers" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
          <el-switch v-model="resourceQuery.warning_only" active-text="仅看延迟" />
          <el-button type="primary" size="small" icon="el-icon-search" @click="filterResources">查询</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="getResourceList">刷新</el-button>
        </div>

        <el-alert
          title="源数据变更由秒级增量扫描发现并立即计算 ARI；内容变化才请求道旅，内容未变化会明确标记为无需推送。1 分钟差异扫描负责失败兜底，10 分钟轮转校准负责防漏。超过 1 分钟标记延迟，超过 10 分钟标记严重延迟。"
          type="info"
          :closable="false"
          show-icon
          class="section-alert"
        />

        <el-table v-loading="resourceLoading" :data="resourceList" border fit highlight-current-row size="small">
          <el-table-column label="状态" min-width="170" align="center">
            <template slot-scope="scope"><el-tag size="mini" :type="resourceTag(scope.row.push_state)">{{ scope.row.push_state_text || scope.row.push_state }}</el-tag></template>
          </el-table-column>
          <el-table-column label="供应商" width="100" align="center"><template slot-scope="scope">{{ supplierName(scope.row.supplier) }}</template></el-table-column>
          <el-table-column prop="source_hotel_code" label="源酒店编码" min-width="145" show-overflow-tooltip />
          <el-table-column label="发布开关" width="90" align="center"><template slot-scope="scope"><el-tag size="mini" :type="scope.row.publish_enabled ? 'success' : 'info'">{{ scope.row.publish_enabled ? '开启' : '关闭' }}</el-tag></template></el-table-column>
          <el-table-column prop="last_ari_source_updated_at" label="平台源更新时间" min-width="165" align="center"><template slot-scope="scope">{{ scope.row.last_ari_source_updated_at || '-' }}</template></el-table-column>
          <el-table-column prop="last_ari_detected_at" label="差异发现时间" min-width="165" align="center"><template slot-scope="scope">{{ scope.row.last_ari_detected_at || '-' }}</template></el-table-column>
          <el-table-column label="发现耗时" width="100" align="right"><template slot-scope="scope">{{ formatSeconds(scope.row.detection_delay_seconds) }}</template></el-table-column>
          <el-table-column prop="last_ari_first_attempt_at" label="首次推送尝试" min-width="165" align="center"><template slot-scope="scope">{{ scope.row.last_ari_first_attempt_at || '-' }}</template></el-table-column>
          <el-table-column label="首次尝试耗时" width="115" align="right"><template slot-scope="scope">{{ formatSeconds(scope.row.first_attempt_delay_seconds) }}</template></el-table-column>
          <el-table-column prop="last_ari_attempt_at" label="最近推送尝试" min-width="165" align="center"><template slot-scope="scope">{{ scope.row.last_ari_attempt_at || '-' }}</template></el-table-column>
          <el-table-column prop="last_ari_success_at" label="道旅接受时间" min-width="165" align="center"><template slot-scope="scope">{{ scope.row.last_ari_success_at || '-' }}</template></el-table-column>
          <el-table-column label="总耗时" width="100" align="right"><template slot-scope="scope">{{ formatSeconds(scope.row.total_delay_seconds) }}</template></el-table-column>
          <el-table-column prop="last_error" label="最近错误" min-width="240" show-overflow-tooltip><template slot-scope="scope">{{ scope.row.last_error || '-' }}</template></el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            :current-page="resourceQuery.page"
            :page-sizes="[20, 50, 100]"
            :page-size="resourceQuery.limit"
            :total="resourceTotal"
            background
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="changeResourceSize"
            @current-change="changeResourcePage"
          />
        </div>
      </el-tab-pane>

      <el-tab-pane label="资源异常历史" name="incident">
        <div class="filter-container monitor-filters">
          <el-input v-model="incidentQuery.hotel_code" clearable size="small" placeholder="源酒店编码" @keyup.enter.native="filterIncidents" />
          <el-select v-model="incidentQuery.supplier" clearable size="small" placeholder="供应商">
            <el-option v-for="item in suppliers" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
          <el-select v-model="incidentQuery.status" clearable size="small" placeholder="异常状态">
            <el-option v-for="item in incidentStatuses" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
          <el-switch v-model="incidentQuery.unresolved_only" active-text="仅看未恢复" />
          <el-button type="primary" size="small" icon="el-icon-search" @click="filterIncidents">查询</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="getIncidentList">刷新</el-button>
        </div>

        <el-alert
          title="每轮资源差异超过 1 分钟只生成一条异常记录；超过 10 分钟升级为严重延迟；推送成功或被后续差异覆盖后关闭。恢复后再次超时会生成新的记录，历史保留 90 天。"
          type="info"
          :closable="false"
          show-icon
          class="section-alert"
        />

        <el-table v-loading="incidentLoading" :data="incidentList" border fit highlight-current-row size="small">
          <el-table-column label="状态" width="150" align="center">
            <template slot-scope="scope"><el-tag size="mini" :type="incidentTag(scope.row.status)">{{ scope.row.status_text || scope.row.status }}</el-tag></template>
          </el-table-column>
          <el-table-column label="供应商" width="100" align="center"><template slot-scope="scope">{{ supplierName(scope.row.supplier) }}</template></el-table-column>
          <el-table-column prop="source_hotel_code" label="源酒店编码" min-width="145" show-overflow-tooltip />
          <el-table-column prop="source_updated_at" label="平台源更新时间" min-width="165" align="center" />
          <el-table-column prop="detected_at" label="差异发现时间" min-width="165" align="center" />
          <el-table-column prop="first_attempt_at" label="首次推送尝试" min-width="165" align="center"><template slot-scope="scope">{{ scope.row.first_attempt_at || '-' }}</template></el-table-column>
          <el-table-column prop="warning_at" label="超过1分钟" min-width="165" align="center" />
          <el-table-column prop="severe_at" label="超过10分钟" min-width="165" align="center"><template slot-scope="scope">{{ scope.row.severe_at || '-' }}</template></el-table-column>
          <el-table-column prop="resolved_at" label="恢复/关闭时间" min-width="165" align="center"><template slot-scope="scope">{{ scope.row.resolved_at || '-' }}</template></el-table-column>
          <el-table-column label="关闭原因" width="130" align="center"><template slot-scope="scope">{{ scope.row.resolution_text || '-' }}</template></el-table-column>
          <el-table-column label="异常时长" width="105" align="right"><template slot-scope="scope">{{ formatSeconds(scope.row.delay_seconds) }}</template></el-table-column>
          <el-table-column prop="last_error" label="最近错误" min-width="240" show-overflow-tooltip><template slot-scope="scope">{{ scope.row.last_error || '-' }}</template></el-table-column>
          <el-table-column label="操作" width="95" fixed="right" align="center">
            <template slot-scope="scope"><el-button type="text" size="mini" @click="showIncidentDetail(scope.row)">请求明细</el-button></template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            :current-page="incidentQuery.page"
            :page-sizes="[20, 50, 100]"
            :page-size="incidentQuery.limit"
            :total="incidentTotal"
            background
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="changeIncidentSize"
            @current-change="changeIncidentPage"
          />
        </div>
      </el-tab-pane>
    </el-tabs>

    <el-dialog title="道旅订单回传请求明细" :visible.sync="detailVisible" width="88%" top="5vh">
      <div v-loading="detailLoading">
        <el-descriptions v-if="selectedJob" :column="4" border size="small" class="detail-summary">
          <el-descriptions-item label="BookingID">{{ selectedJob.booking_id }}</el-descriptions-item>
          <el-descriptions-item label="事件">{{ eventName(selectedJob.event_type) }}</el-descriptions-item>
          <el-descriptions-item label="状态"><el-tag size="mini" :type="feedbackTag(selectedJob.status)">{{ selectedJob.status_text || selectedJob.status }}</el-tag></el-descriptions-item>
          <el-descriptions-item label="尝试次数">{{ selectedJob.attempt_count }}</el-descriptions-item>
          <el-descriptions-item label="最近错误" :span="4">{{ selectedJob.last_error || '-' }}</el-descriptions-item>
        </el-descriptions>
        <el-alert
          v-if="selectedJob && selectedJob.status === 'UNSUPPORTED'"
          title="该事件缺少经过确认的道旅回传接口，系统只记录告警，不会冒用其他接口发送。"
          type="warning"
          :closable="false"
          show-icon
          class="section-alert"
        />
        <el-table :data="feedbackAttempts" border size="small" empty-text="暂无实际请求记录">
          <el-table-column prop="attempt_no" label="次数" width="70" align="center" />
          <el-table-column label="触发" width="85" align="center"><template slot-scope="scope">{{ triggerName(scope.row.trigger_type) }}</template></el-table-column>
          <el-table-column prop="trace_id" label="Trace ID" min-width="190" show-overflow-tooltip />
          <el-table-column label="结果" width="90" align="center"><template slot-scope="scope"><el-tag size="mini" :type="scope.row.success ? 'success' : 'danger'">{{ scope.row.success ? '成功' : '失败' }}</el-tag></template></el-table-column>
          <el-table-column prop="http_status" label="HTTP" width="75" align="center" />
          <el-table-column prop="provider_code" label="业务码" width="80" align="center" />
          <el-table-column prop="started_at" label="开始时间" min-width="165" />
          <el-table-column prop="finished_at" label="完成时间" min-width="165"><template slot-scope="scope">{{ scope.row.finished_at || '-' }}</template></el-table-column>
          <el-table-column prop="error_message" label="错误" min-width="220" show-overflow-tooltip><template slot-scope="scope">{{ scope.row.error_message || '-' }}</template></el-table-column>
          <el-table-column type="expand" width="55">
            <template slot-scope="scope">
              <el-row :gutter="12">
                <el-col :span="12"><b>发送给道旅的内容</b><pre>{{ prettyPayload(scope.row.request_payload) }}</pre></el-col>
                <el-col :span="12"><b>道旅响应内容</b><pre>{{ prettyPayload(scope.row.response_payload) }}</pre></el-col>
              </el-row>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>

    <el-dialog title="道旅资源推送异常明细" :visible.sync="incidentDetailVisible" width="90%" top="5vh">
      <div v-loading="incidentDetailLoading">
        <el-descriptions v-if="selectedIncident" :column="4" border size="small" class="detail-summary">
          <el-descriptions-item label="源酒店编码">{{ selectedIncident.source_hotel_code }}</el-descriptions-item>
          <el-descriptions-item label="供应商">{{ supplierName(selectedIncident.supplier) }}</el-descriptions-item>
          <el-descriptions-item label="状态"><el-tag size="mini" :type="incidentTag(selectedIncident.status)">{{ selectedIncident.status_text || selectedIncident.status }}</el-tag></el-descriptions-item>
          <el-descriptions-item label="异常时长">{{ formatSeconds(selectedIncident.delay_seconds) }}</el-descriptions-item>
          <el-descriptions-item label="平台源更新时间">{{ selectedIncident.source_updated_at }}</el-descriptions-item>
          <el-descriptions-item label="告警时间">{{ selectedIncident.warning_at }}</el-descriptions-item>
          <el-descriptions-item label="恢复时间">{{ selectedIncident.resolved_at || '-' }}</el-descriptions-item>
          <el-descriptions-item label="关闭原因">{{ selectedIncident.resolution_text || '-' }}</el-descriptions-item>
          <el-descriptions-item label="最近错误" :span="4">{{ selectedIncident.last_error || '-' }}</el-descriptions-item>
        </el-descriptions>
        <el-alert
          title="请求/响应明细保留 7 天；异常生命周期保留 90 天，因此较早的异常可能没有请求明细。"
          type="info"
          :closable="false"
          show-icon
          class="section-alert"
        />
        <el-table :data="incidentAttempts" border size="small" empty-text="暂无请求记录或明细已超过7天保留期">
          <el-table-column label="结果" width="80" align="center"><template slot-scope="scope"><el-tag size="mini" :type="scope.row.success ? 'success' : 'danger'">{{ scope.row.success ? '成功' : '失败' }}</el-tag></template></el-table-column>
          <el-table-column prop="attempt_count" label="HTTP尝试" width="85" align="center" />
          <el-table-column prop="trace_id" label="Trace ID" min-width="190" show-overflow-tooltip />
          <el-table-column prop="http_status" label="HTTP" width="75" align="center" />
          <el-table-column prop="provider_code" label="业务码" width="80" align="center" />
          <el-table-column prop="started_at" label="开始时间" min-width="165" />
          <el-table-column prop="finished_at" label="完成时间" min-width="165"><template slot-scope="scope">{{ scope.row.finished_at || '-' }}</template></el-table-column>
          <el-table-column prop="error_message" label="错误" min-width="220" show-overflow-tooltip><template slot-scope="scope">{{ scope.row.error_message || '-' }}</template></el-table-column>
          <el-table-column type="expand" width="55">
            <template slot-scope="scope">
              <el-row :gutter="12">
                <el-col :span="12"><b>发送给道旅的资源内容</b><pre>{{ prettyPayload(scope.row.request_payload) }}</pre></el-col>
                <el-col :span="12"><b>道旅响应内容</b><pre>{{ prettyPayload(scope.row.response_payload) }}</pre></el-col>
              </el-row>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import DidaModuleHeader from './DidaModuleHeader.vue'

export default {
  name: 'DidaPushMonitor',
  components: { DidaModuleHeader },
  data() {
    return {
      activeTab: 'order',
      suppliers: [
        { value: 'jin_jiang', label: '锦江' },
        { value: 'long_teng', label: '龙腾' },
        { value: 'sui_chuan', label: '隧穿亚朵' },
        { value: 'li_cheng', label: '丽呈' },
        { value: 'ya_si_te', label: '雅斯特' },
      ],
      feedbackStatuses: [
        { value: 'PENDING', label: '等待推送' },
        { value: 'PROCESSING', label: '推送中' },
        { value: 'RETRY', label: '等待重推' },
        { value: 'ACCEPTED', label: '道旅接口已接受' },
        { value: 'NOT_REQUIRED', label: '协议无需推送' },
        { value: 'UNSUPPORTED', label: '缺少可用回传接口' },
      ],
      incidentStatuses: [
        { value: 'DELAYED', label: '推送延迟' },
        { value: 'SEVERE_DELAY', label: '严重延迟' },
        { value: 'RESOLVED', label: '已恢复' },
        { value: 'SUPERSEDED', label: '被后续差异合并' },
      ],
      feedbackQuery: { page: 1, limit: 20, booking_id: '', supplier: '', status: '', warning_only: false },
      feedbackList: [],
      feedbackTotal: 0,
      feedbackLoading: false,
      resourceQuery: { page: 1, limit: 20, hotel_code: '', supplier: '', warning_only: false },
      resourceList: [],
      resourceTotal: 0,
      resourceLoading: false,
      incidentQuery: { page: 1, limit: 20, hotel_code: '', supplier: '', status: '', unresolved_only: false },
      incidentList: [],
      incidentTotal: 0,
      incidentLoading: false,
      detailVisible: false,
      detailLoading: false,
      selectedJob: null,
      feedbackAttempts: [],
      incidentDetailVisible: false,
      incidentDetailLoading: false,
      selectedIncident: null,
      incidentAttempts: [],
      retryingJobID: 0,
      refreshTimer: null,
      lastRefreshedAt: '',
      monitorError: '',
    }
  },
  computed: {
    activeLoading() {
      return this.activeTab === 'resource' ? this.resourceLoading : (this.activeTab === 'incident' ? this.incidentLoading : this.feedbackLoading)
    },
    monitorStatus() {
      if (this.monitorError) return 'danger'
      if (this.incidentList.some(item => item.status === 'SEVERE_DELAY')) return 'danger'
      if (this.incidentList.some(item => item.status === 'DELAYED') || this.feedbackList.some(item => item.warning)) return 'warning'
      return 'healthy'
    },
    monitorStatusText() {
      if (this.monitorError) return this.monitorError
      return { danger: '存在严重推送延迟', warning: '存在待关注任务', healthy: '推送链路正常' }[this.monitorStatus]
    },
  },
  created() {
    const bookingID = this.$route && this.$route.query ? this.$route.query.booking_id : ''
    if (typeof bookingID === 'string') this.feedbackQuery.booking_id = bookingID
    this.getFeedbackList()
    this.refreshTimer = setInterval(() => {
      if (!document.hidden && !this.detailVisible && !this.incidentDetailVisible) this.refreshActive(false)
    }, 15000)
  },
  beforeDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  },
  methods: {
    pageData(response) {
      const page = response && response.data ? response.data : {}
      const rows = Array.isArray(page.data) ? page.data : (Array.isArray(page.items) ? page.items : [])
      return { rows, total: Number(page.total || 0) }
    },
    getFeedbackList(showLoading = true) {
      if (showLoading) this.feedbackLoading = true
      this.monitorError = ''
      const params = { ...this.feedbackQuery, warning_only: this.feedbackQuery.warning_only ? 1 : 0 }
      return request({ url: '/api/backend/dida/order-feedback/list', method: 'get', params })
        .then((response) => {
          const page = this.pageData(response)
          this.feedbackList = page.rows
          this.feedbackTotal = page.total
          this.lastRefreshedAt = new Date().toLocaleString()
        })
        .catch(() => { this.monitorError = '订单回传监控读取失败' })
        .finally(() => { this.feedbackLoading = false })
    },
    getResourceList(showLoading = true) {
      if (showLoading) this.resourceLoading = true
      this.monitorError = ''
      const params = { ...this.resourceQuery, warning_only: this.resourceQuery.warning_only ? 1 : 0 }
      return request({ url: '/api/backend/dida/resource-push/list', method: 'get', params })
        .then((response) => {
          const page = this.pageData(response)
          this.resourceList = page.rows
          this.resourceTotal = page.total
          this.lastRefreshedAt = new Date().toLocaleString()
        })
        .catch(() => { this.monitorError = '资源时效监控读取失败' })
        .finally(() => { this.resourceLoading = false })
    },
    getIncidentList(showLoading = true) {
      if (showLoading) this.incidentLoading = true
      this.monitorError = ''
      const params = { ...this.incidentQuery, unresolved_only: this.incidentQuery.unresolved_only ? 1 : 0 }
      return request({ url: '/api/backend/dida/resource-push/incidents', method: 'get', params })
        .then((response) => {
          const page = this.pageData(response)
          this.incidentList = page.rows
          this.incidentTotal = page.total
          this.lastRefreshedAt = new Date().toLocaleString()
        })
        .catch(() => { this.monitorError = '资源异常历史读取失败' })
        .finally(() => { this.incidentLoading = false })
    },
    refreshActive(showLoading = true) {
      if (this.activeTab === 'resource') return this.getResourceList(showLoading)
      if (this.activeTab === 'incident') return this.getIncidentList(showLoading)
      return this.getFeedbackList(showLoading)
    },
    handleTabClick() {
      this.refreshActive()
    },
    filterFeedback() {
      this.feedbackQuery.page = 1
      this.getFeedbackList()
    },
    filterResources() {
      this.resourceQuery.page = 1
      this.getResourceList()
    },
    filterIncidents() {
      this.incidentQuery.page = 1
      this.getIncidentList()
    },
    changeFeedbackSize(value) {
      this.feedbackQuery.limit = value
      this.feedbackQuery.page = 1
      this.getFeedbackList()
    },
    changeFeedbackPage(value) {
      this.feedbackQuery.page = value
      this.getFeedbackList()
    },
    changeResourceSize(value) {
      this.resourceQuery.limit = value
      this.resourceQuery.page = 1
      this.getResourceList()
    },
    changeResourcePage(value) {
      this.resourceQuery.page = value
      this.getResourceList()
    },
    changeIncidentSize(value) {
      this.incidentQuery.limit = value
      this.incidentQuery.page = 1
      this.getIncidentList()
    },
    changeIncidentPage(value) {
      this.incidentQuery.page = value
      this.getIncidentList()
    },
    showFeedbackDetail(row) {
      this.selectedJob = row
      this.feedbackAttempts = []
      this.detailVisible = true
      this.detailLoading = true
      return request({ url: '/api/backend/dida/order-feedback/detail', method: 'get', params: { job_id: row.id } })
        .then((response) => {
          if (response && response.data && response.data.job) this.selectedJob = response.data.job
          this.feedbackAttempts = response && response.data && Array.isArray(response.data.attempts) ? response.data.attempts : []
        })
        .finally(() => { this.detailLoading = false })
    },
    showIncidentDetail(row) {
      this.selectedIncident = row
      this.incidentAttempts = []
      this.incidentDetailVisible = true
      this.incidentDetailLoading = true
      return request({ url: '/api/backend/dida/resource-push/incidents/detail', method: 'get', params: { incident_id: row.id } })
        .then((response) => {
          if (response && response.data && response.data.incident) this.selectedIncident = response.data.incident
          this.incidentAttempts = response && response.data && Array.isArray(response.data.attempts) ? response.data.attempts : []
        })
        .finally(() => { this.incidentDetailLoading = false })
    },
    retryFeedback(row) {
      if (!this.canRetry(row)) {
        this.$message.error((row && row.retry_block_reason) || '当前订单不支持补推订单状态')
        return Promise.resolve()
      }
      return this.$confirm('确认将该事件加入补推队列？系统仍会遵守至少 1 分钟的推送间隔。', '确认补推', { type: 'warning' })
        .then(() => {
          this.retryingJobID = row.id
          return request({ url: '/api/backend/dida/order-feedback/retry', method: 'post', data: { job_id: row.id } })
        })
        .then(() => {
          this.$message.success('已加入补推队列，请关注下次重试时间和请求明细')
          return this.getFeedbackList(false)
        })
        .finally(() => { this.retryingJobID = 0 })
        .catch(() => {})
    },
    canRetry(row) {
      if (!row || !['PENDING', 'RETRY'].includes(row.status)) return false
      if (typeof row.retry_allowed === 'boolean') return row.retry_allowed
      return !this.isImmediateConfirmation(row)
    },
    isImmediateConfirmation(row) {
      return !!row && (row.confirmation_mode === 'IMMEDIATE'
        || ['jin_jiang', 'jinjiang', 'li_cheng', 'ya_si_te'].includes(String(row.supplier || '').toLowerCase()))
    },
    supplierName(value) {
      const match = this.suppliers.find(item => item.value === value)
      return match ? match.label : (value || '-')
    },
    eventName(value) {
      return { order_status: '订单状态', confirmation_code: '酒店确认号' }[value] || value || '-'
    },
    eventValue(row) {
      if (!row) return '-'
      if (row.event_type === 'confirmation_code') return '确认号'
      return { 1: '确认', 2: '取消', 3: '拒绝' }[Number(row.desired_status)] || String(row.desired_status || '-')
    },
    triggerName(value) {
      return { auto: '自动', manual: '人工' }[value] || value || '-'
    },
    feedbackTag(value) {
      return { PENDING: 'warning', PROCESSING: '', RETRY: 'warning', ACCEPTED: 'success', NOT_REQUIRED: 'info', UNSUPPORTED: 'danger' }[value] || 'info'
    },
    resourceTag(value) {
      return { SYNCHRONIZED: 'success', UNCHANGED: 'info', PENDING: 'warning', PUSHING: '', DELAYED: 'warning', SEVERE_DELAY: 'danger' }[value] || 'info'
    },
    incidentTag(value) {
      return { DELAYED: 'warning', SEVERE_DELAY: 'danger', RESOLVED: 'success', SUPERSEDED: 'info' }[value] || 'info'
    },
    formatSeconds(value) {
      if (value === null || value === undefined || value === '') return '-'
      const seconds = Math.max(0, Number(value) || 0)
      if (seconds < 60) return `${seconds}秒`
      if (seconds < 3600) return `${Math.floor(seconds / 60)}分${seconds % 60}秒`
      return `${Math.floor(seconds / 3600)}时${Math.floor((seconds % 3600) / 60)}分`
    },
    prettyPayload(value) {
      if (!value) return '-'
      if (typeof value !== 'string') return JSON.stringify(value, null, 2)
      try {
        return JSON.stringify(JSON.parse(value), null, 2)
      } catch (error) {
        return value
      }
    },
  },
}
</script>

<style scoped>
.page-alert,
.section-alert { margin-bottom: 14px; }
.monitor-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.monitor-filters .el-input,
.monitor-filters .el-select { width: 190px; }
.retry-disabled-text {
  display: inline-block;
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}
.detail-summary { margin-bottom: 14px; }
pre {
  margin: 8px 0 0;
  padding: 12px;
  max-height: 360px;
  overflow: auto;
  color: #303133;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
